import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../services/api';

/**
 * Custom hook for fetching and managing meetings inbox from iConnections.
 * Can be used in any component that needs access to sent, received, or confirmed meetings.
 * 
 * @param {string} eventName - The event name to fetch meetings for (e.g., "Global Alts Miami 2026")
 * @param {object} options - Optional configuration
 * @param {boolean} options.autoFetch - Whether to automatically fetch on mount (default: true)
 * @param {boolean} options.fetchOnEventChange - Whether to refetch when eventName changes (default: true)
 * @param {number} options.eventId - F4C event ID - if provided, filter to only show meetings with confirmed contacts
 * @param {number} options.icEventId - iConnections Event ID (required if useSqlDirect is true)
 * @param {boolean} options.useSqlDirect - If true, use SQL Server direct query instead of iConnections API (default: false)
 * 
 * @returns {object} - Meetings state, helper functions, and utilities
 */
const useMeetingsInbox = (eventName, options = {}) => {
    const { 
        autoFetch = true, 
        fetchOnEventChange = true, 
        eventId = null,
        icEventId = null,
        useSqlDirect = false  // Set to true to use SQL Server direct query instead of iConnections API
    } = options;

    const [meetings, setMeetings] = useState({
        sent: [],
        received: [],
        confirmed: [],
        loading: false,
        loaded: false,
        error: null,
        userEntityType: null,
        availableEntityTypes: []
    });

    /**
     * Fetch all meetings (sent, received, confirmed) from iConnections
     * If useSqlDirect is true, uses SQL Server direct query instead of iConnections API
     */
    const fetchAllMeetings = useCallback(async (overrideEventName = null, overrideEventId = null, overrideIcEventId = null) => {
        const targetEventName = overrideEventName || eventName;
        const targetEventId = overrideEventId || eventId;
        const targetIcEventId = overrideIcEventId || icEventId;
        
        // For SQL direct, we need eventId and icEventId
        // For iConnections API, we need eventName
        if (useSqlDirect) {
            if (!targetEventId || !targetIcEventId) {
                console.warn('useMeetingsInbox: SQL direct mode requires eventId and icEventId');
                return;
            }
        } else {
            if (!targetEventName) {
                console.warn('useMeetingsInbox: No event name provided');
                return;
            }
        }

        setMeetings(prev => ({ ...prev, loading: true, error: null }));

        try {
            let response;
            
            if (useSqlDirect) {
                // Use SQL Server direct query (potentially more performant)
                console.log('[useMeetingsInbox] Using SQL DIRECT mode - eventId:', targetEventId, 'icEventId:', targetIcEventId);
                response = await apiService.getAllMeetingsSqlDirect(targetEventId, targetIcEventId);
            } else {
                // Use iConnections API (original method)
                console.log('[useMeetingsInbox] Using iConnections API - event:', targetEventName, 'eventId:', targetEventId);
                response = await apiService.getAllMeetings(targetEventName, targetEventId);
            }

            if (response.data.success) {
                const newMeetings = {
                    sent: response.data.sent || [],
                    received: response.data.received || [],
                    confirmed: response.data.confirmed || [],
                    loading: false,
                    loaded: true,
                    error: response.data.error || null, // Partial errors
                    userEntityType: response.data.userEntityType || null,
                    availableEntityTypes: response.data.availableEntityTypes || []
                };
                setMeetings(newMeetings);
                
                console.log('[useMeetingsInbox] Meetings loaded:', {
                    mode: useSqlDirect ? 'SQL_DIRECT' : 'IC_API',
                    sent: newMeetings.sent.length,
                    received: newMeetings.received.length,
                    confirmed: newMeetings.confirmed.length
                });
                
                return newMeetings;
            } else {
                const errorState = {
                    ...meetings,
                    loading: false,
                    loaded: true,
                    error: response.data.error || 'Failed to load meetings'
                };
                setMeetings(errorState);
                console.error('[useMeetingsInbox] Failed to load meetings:', response.data.error);
                return errorState;
            }
        } catch (err) {
            console.error('[useMeetingsInbox] Error fetching meetings:', err);
            const errorState = {
                ...meetings,
                loading: false,
                loaded: true,
                error: 'Failed to fetch meetings from server'
            };
            setMeetings(errorState);
            return errorState;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventName, eventId, icEventId, useSqlDirect]);

    /**
     * Fetch only sent meetings (with pagination support)
     */
    const fetchSentMeetings = useCallback(async (page = 1, size = 50) => {
        if (!eventName) return null;
        
        try {
            const response = await apiService.getSentMeetings(eventName, page, size);
            if (response.data.success) {
                setMeetings(prev => ({
                    ...prev,
                    sent: page === 1 
                        ? response.data.data 
                        : [...prev.sent, ...response.data.data]
                }));
                return response.data;
            }
            return null;
        } catch (err) {
            console.error('[useMeetingsInbox] Error fetching sent meetings:', err);
            return null;
        }
    }, [eventName]);

    /**
     * Fetch only received meetings (with pagination support)
     */
    const fetchReceivedMeetings = useCallback(async (page = 1, size = 50) => {
        if (!eventName) return null;
        
        try {
            const response = await apiService.getReceivedMeetings(eventName, page, size);
            if (response.data.success) {
                setMeetings(prev => ({
                    ...prev,
                    received: page === 1 
                        ? response.data.data 
                        : [...prev.received, ...response.data.data]
                }));
                return response.data;
            }
            return null;
        } catch (err) {
            console.error('[useMeetingsInbox] Error fetching received meetings:', err);
            return null;
        }
    }, [eventName]);

    /**
     * Fetch only confirmed meetings
     */
    const fetchConfirmedMeetings = useCallback(async () => {
        if (!eventName) return null;
        
        try {
            const response = await apiService.getConfirmedMeetings(eventName);
            if (response.data.success) {
                setMeetings(prev => ({
                    ...prev,
                    confirmed: response.data.data
                }));
                return response.data;
            }
            return null;
        } catch (err) {
            console.error('[useMeetingsInbox] Error fetching confirmed meetings:', err);
            return null;
        }
    }, [eventName]);

    /**
     * Get meeting status for a specific contact by their ic_contact_id
     * Returns: { status: 'confirmed' | 'sent' | 'received', meeting: object } or null
     */
    const getMeetingStatusForContact = useCallback((icContactId) => {
        if (!icContactId) return null;

        // Check confirmed meetings first (highest priority)
        const confirmedMeeting = meetings.confirmed.find(meeting =>
            meeting.attendees?.some(a => a.contactId === icContactId) ||
            meeting.organizer?.contactId === icContactId
        );
        if (confirmedMeeting) {
            return { status: 'confirmed', meeting: confirmedMeeting };
        }

        // Check sent meetings (we sent a request to this contact)
        const sentMeeting = meetings.sent.find(meeting =>
            meeting.attendees?.some(a => a.contactId === icContactId)
        );
        if (sentMeeting) {
            return { status: 'sent', meeting: sentMeeting };
        }

        // Check received meetings (this contact sent us a request)
        const receivedMeeting = meetings.received.find(meeting =>
            meeting.organizer?.contactId === icContactId
        );
        if (receivedMeeting) {
            return { status: 'received', meeting: receivedMeeting };
        }

        return null;
    }, [meetings.confirmed, meetings.sent, meetings.received]);

    /**
     * Check if a contact has any meeting interaction
     */
    const hasMeetingWithContact = useCallback((icContactId) => {
        return getMeetingStatusForContact(icContactId) !== null;
    }, [getMeetingStatusForContact]);

    /**
     * Get all meetings for a specific contact
     */
    const getMeetingsForContact = useCallback((icContactId) => {
        if (!icContactId) return { sent: [], received: [], confirmed: [] };

        return {
            sent: meetings.sent.filter(m => 
                m.attendees?.some(a => a.contactId === icContactId)
            ),
            received: meetings.received.filter(m => 
                m.organizer?.contactId === icContactId
            ),
            confirmed: meetings.confirmed.filter(m =>
                m.attendees?.some(a => a.contactId === icContactId) ||
                m.organizer?.contactId === icContactId
            )
        };
    }, [meetings.sent, meetings.received, meetings.confirmed]);

    /**
     * Get meeting status for a company by their EventCompanyId
     * Returns: { 
     *   status: 'confirmed' | 'pending_sent' | 'pending_received' | null,
     *   meeting: object,
     *   isOrganizer: boolean,
     *   meetingStatusId: number
     * } or null
     */
    const getMeetingStatusForCompany = useCallback((eventCompanyId) => {
        if (!eventCompanyId) return null;

        // Check confirmed meetings first (highest priority)
        const confirmedMeeting = meetings.confirmed.find(meeting =>
            meeting.organizerEventCompanyId === eventCompanyId ||
            meeting.participantEventCompanyId === eventCompanyId
        );
        if (confirmedMeeting) {
            const isOrganizer = confirmedMeeting.organizerEventCompanyId !== eventCompanyId;
            return { 
                status: 'confirmed', 
                meeting: confirmedMeeting, 
                isOrganizer,
                meetingStatusId: confirmedMeeting.statusId || 2
            };
        }

        // Check sent meetings (we sent a request to this company - they are the participant)
        const sentMeeting = meetings.sent.find(meeting =>
            meeting.participantEventCompanyId === eventCompanyId
        );
        if (sentMeeting) {
            return { 
                status: 'pending_sent', 
                meeting: sentMeeting, 
                isOrganizer: true,
                meetingStatusId: sentMeeting.statusId || 1
            };
        }

        // Check received meetings (this company sent us a request - they are the organizer)
        const receivedMeeting = meetings.received.find(meeting =>
            meeting.organizerEventCompanyId === eventCompanyId
        );
        if (receivedMeeting) {
            return { 
                status: 'pending_received', 
                meeting: receivedMeeting, 
                isOrganizer: false,
                meetingStatusId: receivedMeeting.statusId || 1
            };
        }

        return null;
    }, [meetings.confirmed, meetings.sent, meetings.received]);

    /**
     * Check if a company has any meeting interaction
     */
    const hasMeetingWithCompany = useCallback((eventCompanyId) => {
        return getMeetingStatusForCompany(eventCompanyId) !== null;
    }, [getMeetingStatusForCompany]);

    /**
     * Refresh all meetings data
     */
    const refresh = useCallback(() => {
        return fetchAllMeetings();
    }, [fetchAllMeetings]);

    /**
     * Clear all meetings data
     */
    const clear = useCallback(() => {
        setMeetings({
            sent: [],
            received: [],
            confirmed: [],
            loading: false,
            loaded: false,
            error: null
        });
    }, []);

    // Computed values
    const totalMeetings = useMemo(() => 
        meetings.sent.length + meetings.received.length + meetings.confirmed.length,
        [meetings.sent.length, meetings.received.length, meetings.confirmed.length]
    );

    const pendingActionCount = useMemo(() => {
        // Count meetings that need user action (e.g., received requests not yet responded to)
        return meetings.received.filter(m => 
            m.statusId === 1 || m.status === 'Pending'
        ).length;
    }, [meetings.received]);

    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch && eventName) {
            fetchAllMeetings();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Refetch when eventName changes if enabled
    useEffect(() => {
        if (fetchOnEventChange && eventName && meetings.loaded) {
            fetchAllMeetings();
        }
    }, [eventName]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        // State
        sent: meetings.sent,
        received: meetings.received,
        confirmed: meetings.confirmed,
        loading: meetings.loading,
        loaded: meetings.loaded,
        error: meetings.error,
        userEntityType: meetings.userEntityType,
        availableEntityTypes: meetings.availableEntityTypes,
        
        // Computed
        totalMeetings,
        pendingActionCount,
        
        // Actions
        fetchAllMeetings,
        fetchSentMeetings,
        fetchReceivedMeetings,
        fetchConfirmedMeetings,
        refresh,
        clear,
        
        // Utilities - Contact-based
        getMeetingStatusForContact,
        hasMeetingWithContact,
        getMeetingsForContact,
        
        // Utilities - Company-based (EventCompanyId)
        getMeetingStatusForCompany,
        hasMeetingWithCompany
    };
};

export default useMeetingsInbox;

